import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import {
  useCancelJobRun,
  useClearFinishedJobRuns,
  useDeleteJobRun,
  useJobRuns,
} from '@/admin-design/_common/jobs/api'
import type { JobRun, JobSummary } from '@/admin-design/_common/jobs/types'
import { count } from '@/lib/money'
import { syncDetails } from '@/lib/spend'
import { jobLabel, TONE_CLASS, type Tone } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Button, ConfirmDialog, DataTable, EmptyState, ErrorState, IconButton, PageHeader, StatusDot, StatusPill, When, useIsDesktop, useQueryParam, useToast, errorText, type Column } from '@/ui'

function StatusCell({ status }: { status: string }) {
  return <StatusPill domain="job" status={status} />
}

const PAGE = 50

const TRIGGER: Record<string, string> = { manual: '수동', schedule: '예약' }
const triggerText = (t: string) => TRIGGER[t] ?? t

/** What a run says in the 내용 column: a summary line in our words, and the
 *  service's own error words, whole, to be drawn in mono on one cut line. A
 *  sync pass writes its result into details_json (internal/sync
 *  Result.DetailsJSON): the AdMob row count, the Ads rows or its refusal, and
 *  how long the pass took. */
export interface RunDetail {
  text: string
  /** The service's raw error lines (AdMob, Google Ads). */
  errors: string[]
}

export function runDetail(run: JobRun): RunDetail {
  if (run.status === 'error') return { text: run.message || '오류', errors: [] }
  if (run.job === 'sync') {
    const d = syncDetails(run)
    if (d) {
      const parts: string[] = []
      if (d.admob_skipped) parts.push('AdMob 건너뜀')
      else if (d.admob_error) parts.push('AdMob 오류')
      else parts.push(`AdMob ${count(d.admob_rows ?? 0)}행`)
      if (d.ads_error) parts.push('Google Ads 접근 거부')
      else if (d.ads_ok) parts.push(`Google Ads ${count(d.ads_rows ?? 0)}행`)
      if (typeof d.duration_ms === 'number' && d.duration_ms > 0) parts.push(`소요 ${formatDuration(d.duration_ms)}`)
      return { text: parts.join(' · '), errors: [d.admob_error, d.ads_error].filter((e): e is string => !!e) }
    }
  }
  return { text: run.message || '—', errors: [] }
}

const HANGUL = /[가-힣]/

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)}초`
  const m = Math.floor(s / 60)
  return `${m}분 ${Math.floor(s % 60)}초`
}

function SummaryCard({ s, active, onPick }: { s: JobSummary; active: boolean; onPick: () => void }) {
  const desktop = useIsDesktop()
  if (!desktop) return <SummaryLine s={s} active={active} onPick={onPick} />
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        'rounded-lg border bg-card p-3 text-left transition-colors hover:border-foreground/25',
        active ? 'border-foreground' : 'border-border',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 truncate text-body font-medium">{jobLabel(s.job)}</span>
        <StatusCell status={s.last_status} />
      </div>
      <dl className="mt-2.5 grid grid-cols-3 gap-2 text-center">
        {/* only the number that is a problem takes a colour */}
        <Count label="24시간 성공" value={s.ok_24h} />
        <Count label="24시간 오류" value={s.error_24h} tone={s.error_24h > 0 ? 'failed' : undefined} />
        <Count label="실행 중" value={s.running} />
      </dl>
      <div className="mt-2.5 text-meta text-muted-foreground">
        {s.last_run_at ? (
          <>
            마지막 <When iso={s.last_run_at} mode="relative" />
          </>
        ) : (
          '아직 실행하지 않았다'
        )}
      </div>
    </button>
  )
}

/** The phone's card: two lines instead of a block of three boxes, so eight
 *  jobs fit on a screen and the runs below are reachable. */
function SummaryLine({ s, active, onPick }: { s: JobSummary; active: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        'rounded-lg border bg-card px-3 py-2.5 text-left transition-colors',
        active ? 'border-foreground' : 'border-border',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-body font-medium">{jobLabel(s.job)}</span>
        <StatusCell status={s.last_status} />
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-2 text-meta tabular-nums text-muted-foreground">
        <span>성공 {s.ok_24h}</span>
        <span className="inline-flex items-center gap-1">
          {s.error_24h > 0 && <StatusDot tone="failed" />}오류 {s.error_24h}
        </span>
        {s.running > 0 && (
          <span className="inline-flex items-center gap-1">
            <StatusDot tone="publishing" />
            실행 중 {s.running}
          </span>
        )}
        <span>
          {s.last_run_at ? (
            <>
              마지막 <When iso={s.last_run_at} mode="relative" />
            </>
          ) : (
            '아직 실행하지 않았다'
          )}
        </span>
      </div>
    </button>
  )
}

/** One count of the summary. The number keeps the text colour — one failed
 *  run in 24 hours is not a red headline; a dot beside it says which kind. */
function Count({ label, value, tone }: { label: string; value: number; tone?: Tone }) {
  return (
    <div className="rounded-md bg-muted/60 py-1.5">
      <dd className={cn('inline-flex items-center gap-1.5 text-section font-semibold tabular-nums', value === 0 && 'text-muted-foreground')}>
        {value > 0 && tone && <StatusDot tone={tone} />}
        {value}
      </dd>
      <dt className="text-micro text-muted-foreground">{label}</dt>
    </div>
  )
}

/** /jobs: what the background jobs did. The summary and the runs both come
 *  from the jobs engine; the filter lives in the address (`?job=`), which the
 *  desk notices link to. */
/** A run's message: our Korean summary in body type, the service's raw
 *  words in mono. The text keeps the body colour — the status pill beside it
 *  says the run failed. */
function RunMessage({ r, clamp }: { r: JobRun; clamp?: boolean }) {
  const { text, errors } = runDetail(r)
  return (
    <div className="min-w-0 space-y-0.5">
      <div
        title={clamp ? text : undefined}
        className={cn(
          'break-words text-meta',
          clamp ? 'line-clamp-2' : 'whitespace-pre-wrap',
          !HANGUL.test(text) && 'font-mono',
          r.status === 'cancelled' ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {text}
      </div>
      {errors.map((e, i) => (
        <div key={i} title={e} className="max-w-[28rem] truncate font-mono text-meta text-foreground">
          {e}
        </div>
      ))}
    </div>
  )
}

export default function Jobs() {
  const [job, setJob] = useQueryParam('job')
  const toast = useToast()
  // A linked job shows its last 50 runs; the unfiltered list is the latest 200.
  const q = useJobRuns({ job: job || undefined, limit: job ? 50 : 200, refetchMs: 15_000 })
  const cancel = useCancelJobRun()
  const del = useDeleteJobRun()
  const clear = useClearFinishedJobRuns()
  const [deleting, setDeleting] = useState<JobRun | null>(null)
  const [clearing, setClearing] = useState(false)
  // 50 at a time; 더 보기 adds the next 50. A new filter starts again at 50.
  const [more, setMore] = useState({ job, shown: PAGE })
  const shown = more.job === job ? more.shown : PAGE

  const runs = q.data?.runs
  const summary = q.data?.summary ?? []
  // The chips come from the summary, which the server builds from every job
  // that has ever run; a linked job with no runs is still a chip.
  const keys = summary.map((s) => s.job)
  const chips = job && !keys.includes(job) ? [...keys, job] : keys

  const columns: Column<JobRun>[] = [
    { key: 'job', header: '작업', card: 'title', cell: (r) => <span className="font-medium">{jobLabel(r.job)}</span> },
    { key: 'status', header: '상태', card: 'meta', cell: (r) => <StatusCell status={r.status} /> },
    {
      key: 'trigger',
      header: '실행',
      card: 'hidden',
      cell: (r) => <span className="text-muted-foreground">{triggerText(r.trigger)}</span>,
    },
    {
      key: 'started',
      header: '시작',
      // On the phone the start, how it ran and how long go on one line under
      // the job's name; the desktop table keeps them in their own columns.
      card: 'sub',
      cell: (r) => (
        <>
          <When iso={r.started_at} mode="absolute" className="tabular-nums text-muted-foreground" />
          <span className="text-muted-foreground md:hidden">
            {' · '}
            {triggerText(r.trigger)}
            {r.duration_ms > 0 && r.status !== 'running' && ` · ${formatDuration(r.duration_ms)}`}
          </span>
        </>
      ),
    },
    {
      key: 'dur',
      header: '걸린 시간',
      align: 'right',
      cardHide: (r) => r.status !== 'running',
      cell: (r) =>
        r.status === 'running' ? (
          <span className={TONE_CLASS.publishing.text}>진행 중</span>
        ) : r.duration_ms > 0 ? (
          <span className="tabular-nums">{formatDuration(r.duration_ms)}</span>
        ) : (
          '—'
        ),
    },
    {
      key: 'msg',
      header: '내용',
      className: 'max-w-[28rem]',
      card: 'hidden',
      cell: (r) => <RunMessage r={r} />,
    },
    // The phone's card: the same words as a plain line under the time, two
    // lines at most (the whole text is in its title), and nothing at all for
    // a run with nothing to say — a labelled "내용" row made every card a
    // block, and a 429 ran to seven red lines.
    {
      key: 'msgCard',
      header: '내용',
      cardOnly: true,
      card: 'sub',
      cell: (r) => (runDetail(r).text === '—' && runDetail(r).errors.length === 0 ? null : <RunMessage r={r} clamp />),
    },
    {
      key: 'act',
      header: <span className="sr-only">동작</span>,
      align: 'right',
      // Beside the status on a phone card: a whole footer row for one small
      // button doubled the length of the run list.
      card: 'meta',
      cell: (r) =>
        r.status === 'running' ? (
          <Button size="sm" loading={cancel.isPending && cancel.variables === r.id} onClick={() => cancel.mutate(r.id)}>
            중지
          </Button>
        ) : (
          <IconButton icon={Trash2} size="sm" variant="quietDanger" label={`기록 #${r.id} 삭제`} onClick={() => setDeleting(r)} />
        ),
    },
  ]

  return (
    <>
      <PageHeader
        title="작업 기록"
        description="백그라운드 작업이 언제 무엇을 했는지. 동기화는 매시간 돈다. 15초마다 새로 읽는다."
        actions={
          <Button onClick={() => setClearing(true)} loading={clear.isPending}>
            끝난 기록 지우기
          </Button>
        }
      />

      {q.error ? (
        <ErrorState title="작업 기록을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div className="space-y-5">
          {summary.length > 0 && (
            <section aria-label="작업별 요약" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {summary.map((s) => (
                <SummaryCard key={s.job} s={s} active={job === s.job} onPick={() => setJob(job === s.job ? '' : s.job)} />
              ))}
            </section>
          )}

          {chips.length > 0 && (
            <div role="group" aria-label="작업 거르기" className="flex flex-wrap items-center gap-1.5">
              {['', ...chips].map((k) => (
                <button
                  key={k || 'all'}
                  type="button"
                  aria-pressed={job === k}
                  onClick={() => setJob(k)}
                  className={cn(
                    'h-7 rounded-full border px-2.5 text-meta transition-colors',
                    job === k
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-card text-muted-foreground hover:text-foreground',
                  )}
                >
                  {k ? jobLabel(k) : '전체'}
                </button>
              ))}
            </div>
          )}

          <section>
            <h2 className="mb-2 text-section font-semibold">
              {job ? `${jobLabel(job)} · 최근 50회` : '실행 기록'}
              {runs && <span className="ml-2 text-table font-normal text-muted-foreground">{runs.length}건</span>}
            </h2>
            <DataTable
              label="실행 기록"
              columns={columns}
              rows={runs?.slice(0, shown)}
              rowKey={(r) => r.id}
              loading={q.isLoading}
              empty={
                <EmptyState
                  title={job ? '이 작업의 실행 기록이 없다' : '아직 실행 기록이 없다'}
                  description={job ? undefined : '작업이 한 번 돌고 나면 여기에 쌓인다.'}
                />
              }
            />
            {runs && runs.length > shown && (
              <div className="mt-3 flex justify-center">
                <Button size="sm" onClick={() => setMore({ job, shown: shown + PAGE })}>
                  더 보기 · {count(runs.length - shown)}건 남음
                </Button>
              </div>
            )}
          </section>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`실행 기록 #${deleting?.id ?? ''}을 지울까`}
        confirmLabel="삭제"
        confirmVariant="destructive"
        onConfirm={async () => {
          if (!deleting) return
          try {
            await del.mutateAsync(deleting.id)
          } catch (e) {
            toast.error(`지우지 못했다: ${errorText(e)}`)
          }
          setDeleting(null)
        }}
      />
      <ConfirmDialog
        open={clearing}
        onOpenChange={setClearing}
        title="끝난 기록을 모두 지울까"
        description="성공·오류·중지·건너뜀 기록이 지워진다. 실행 중인 것은 남는다."
        confirmLabel="모두 삭제"
        confirmVariant="destructive"
        onConfirm={async () => {
          try {
            const r = await clear.mutateAsync()
            toast.success(`${r.deleted}건을 지웠다`)
          } catch (e) {
            toast.error(`지우지 못했다: ${errorText(e)}`)
          }
          setClearing(false)
        }}
      />
    </>
  )
}
